import { STORAGE_KEYS, STORAGE_NAMESPACE } from './zustand-store'

export const CURRENT_SCHEMA_VERSION = 1 as const
export type SchemaVersion = typeof CURRENT_SCHEMA_VERSION

export type MigrationOutcome =
  | { ok: true; from: number | null; to: SchemaVersion }
  | { ok: false; from: number; to: SchemaVersion; reason: string }

const BACKUP_KEY_PREFIX = `${STORAGE_NAMESPACE}__backup_v`

/**
 * Inspects the persisted schemaVersion in localStorage and runs migrations
 * up to CURRENT_SCHEMA_VERSION. In Phase 2 only v1 is defined, so the
 * outcomes are:
 *
 *  - null     → first run; sets v1 and returns ok
 *  - v1       → no-op; returns ok
 *  - > v1     → returns error (storage newer than app)
 *  - other    → returns error (no migration available)
 *
 * Future phases extend this with a chain of pure functions in src/core/storage.
 */
export function loadAndMigrate(): MigrationOutcome {
  const stored = readStoredVersion()

  if (stored === null) {
    writeStoredVersion(CURRENT_SCHEMA_VERSION)
    return { ok: true, from: null, to: CURRENT_SCHEMA_VERSION }
  }

  if (stored === CURRENT_SCHEMA_VERSION) {
    return { ok: true, from: stored, to: CURRENT_SCHEMA_VERSION }
  }

  if (stored > CURRENT_SCHEMA_VERSION) {
    return {
      ok: false,
      from: stored,
      to: CURRENT_SCHEMA_VERSION,
      reason: 'storage schema is newer than this app version',
    }
  }

  return {
    ok: false,
    from: stored,
    to: CURRENT_SCHEMA_VERSION,
    reason: `no migration available from v${stored} to v${CURRENT_SCHEMA_VERSION}`,
  }
}

function readStoredVersion(): number | null {
  const raw = localStorage.getItem(STORAGE_KEYS.schemaVersion)
  if (raw === null) return null
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) ? parsed : null
}

function writeStoredVersion(version: SchemaVersion): void {
  localStorage.setItem(STORAGE_KEYS.schemaVersion, String(version))
}

/**
 * Backs up every namespaced key under `__backup_v{n}:` before destructive ops.
 * Returns the prefix used (caller can list keys later via the same prefix).
 */
export function backupNamespacedKeys(targetVersion: number): string {
  const prefix = `${BACKUP_KEY_PREFIX}${targetVersion}:`
  for (const key of listNamespacedKeys()) {
    const value = localStorage.getItem(key)
    if (value !== null) {
      localStorage.setItem(`${prefix}${key}`, value)
    }
  }
  return prefix
}

/**
 * Removes every key under the `folha-fiscal:` namespace. Backups under
 * `folha-fiscal:__backup_*` are also removed; callers wanting to keep them
 * should snapshot first.
 *
 * This is the underlying op for the "Apagar tudo" flow (RF-10) and for the
 * paranoid-mode TTL guard.
 */
export function wipeAll(): void {
  for (const key of listNamespacedKeys()) {
    localStorage.removeItem(key)
  }
}

function listNamespacedKeys(): string[] {
  const keys: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key !== null && key.startsWith(STORAGE_NAMESPACE)) keys.push(key)
  }
  return keys
}
